import type { MembershipPlanId } from '../types/practitionerSignup'

export const foundingOffer = {
  deadline: 'October 31, 2026',
  headline: 'Join before October 31 and get 2 months free',
  subline:
    'Founding members pay no fees for their first two months, then stay at their founding rate — locked in for life.',
}

/** Founding Member promo copy for the membership page. */
export const foundingPromo = {
  headline: 'Join by October 31 and get your first 2 months free.',
  body: 'Join Fem Entity as a Founding Member by October 31 and your first two months are complimentary. Your regular membership rate begins in Month 3 and remains locked in while your membership stays active.',
}

export interface FoundingBenefit {
  title: string
  description: string
}

export const foundingBenefits: FoundingBenefit[] = [
  {
    title: '2 months free',
    description:
      'No fees. Build your profile and receive referrals from day one.',
  },
  {
    title: 'Rate locked for life',
    description:
      'Your founding rate is permanent — as long as your subscription is active.',
  },
  {
    title: 'Free verification',
    description:
      'A $25 value, covered for founding members. Your credentials verified before you go live — at no cost to you.',
  },
  {
    title: 'Founding Member recognition',
    description:
      'Permanently on your profile — a lasting signal of trust and early commitment.',
  },
]

export const foundingPerks: string[] = [
  'Founding Member recognition on your profile',
  'Named in launch communications',
  'Social feature on Fem Entity channels',
  'Priority access to new features',
  'Early client matching from our waitlist',
  'Direct input into platform development',
]

export interface FoundingPlan {
  /** Sent to the checkout Edge Function; mapped to a Stripe Price server-side. */
  id: MembershipPlanId
  name: string
  seats: string
  price: string
  priceSuffix: string
  note?: string
  features: string[]
  highlighted?: boolean
}

export const foundingPlans: FoundingPlan[] = [
  {
    id: 'anchor',
    name: 'Anchor',
    seats: '1 practitioner seat',
    price: '$39.99',
    priceSuffix: '/mo',
    features: [
      'Practitioner profile with booking tools',
      'Client management & scheduling',
      'Founding Member recognition + social feature',
    ],
  },
  {
    id: 'bloom',
    name: 'Bloom',
    seats: '3–5 practitioner seats',
    price: '$119.99',
    priceSuffix: '/mo',
    note: 'Ideal for group practices or practitioners who collaborate.',
    highlighted: true,
    features: [
      'Shared scheduling & client management across your team',
      'Every team member gets their own profile and visibility',
      'Founding Member recognition for all seats',
    ],
  },
  {
    id: 'thrive',
    name: 'Thrive',
    seats: '6–10 practitioner seats',
    price: '$274.99',
    priceSuffix: '/mo',
    features: [
      'Advanced team management tools',
      'Enhanced visibility & priority support',
      'Early enterprise partnership access',
    ],
  },
  {
    id: 'collective',
    name: 'Collective',
    seats: '10+ practitioner seats',
    price: '$274.99',
    priceSuffix: ' + $24.99/seat',
    features: [
      'Everything in Thrive',
      'Scalable, unlimited seat access',
      'Custom enterprise onboarding',
      'Organization-level founding recognition',
    ],
  },
]

export const foundingMembershipMessage =
  'Your first two months are complimentary. Your Founding Member rate begins in month three and remains locked in while your membership remains active.'

/** "Get more from your Founding Membership" section. */
export const foundingMembershipValue = {
  eyebrow: 'Get more from your Founding Membership',
  heading: 'Your practice. Your network. Your voice.',
  intro:
    'Founding Members get more than an early rate. You’ll have direct access to the Fem Entity team, opportunities to connect with other practitioners, and a voice in how the network evolves to better support your practice.',
  lead: 'During your first two complimentary months, you’ll:',
  perksHeading: 'Founding early has its perks.',
  perksBody:
    'Your Founding Member rate stays locked in for as long as your membership remains active. As Fem Entity grows, you keep the rate you joined at.',
}

export type FoundingMemberStepIcon = 'profile' | 'onboarding' | 'community' | 'rate'

export interface FoundingMemberStep {
  icon: FoundingMemberStepIcon
  title: string
  description: string
  optional?: boolean
}

export const foundingMemberSteps: FoundingMemberStep[] = [
  {
    icon: 'profile',
    title: 'Build your presence on Fem Entity',
    description:
      'Complete your practitioner profile within 14 days so women looking for care can discover your services.',
  },
  {
    icon: 'onboarding',
    title: 'Get personally onboarded',
    optional: true,
    description:
      'Join one 30-minute virtual onboarding session designed to help you make the most of your profile, matching, booking, and practitioner tools.',
  },
  {
    icon: 'community',
    title: 'Have a direct line to Fem Entity',
    description:
      'Share feedback through a feedback session, practitioner survey, or our practitioner community — so you can tell us what would make the network more valuable to your practice.',
  },
  {
    icon: 'rate',
    title: 'Lock in your Founding Member rate',
    description:
      'Maintain an active membership from Month 3 onward and keep your preferred Founding Member rate.',
  },
]

/** Practitioner Coffee Chats with Michelle Gibson. */
export const coffeeChat = {
  registrationUrl:
    'https://us06web.zoom.us/meeting/register/GneOL5eUT-W71NCg1-Xyjg#/registration',
  hostPhotoUrl:
    'https://cdn.magicpatterns.com/uploads/5TdpmAwE2D3wPvzUnw7PG2/T05795C5G0L-U09VCS2GBK7-bbb7897438fa-512.jpg' as
      | string
      | null,
  hostName: 'Michelle Gibson',
  hostTitle: 'Fem Entity Advisor & counselling practice owner',
}
